<template>
  <div>
    <v-container v-if="$apollo.loading">
      <AppLoadingPage />
    </v-container>
    <v-container v-else class="container-content">
      <v-form class="no-gutters" :disabled="formSubmitted" @keyup.enter.prevent>
        <v-col cols="12" md="8">
          <div v-if="information" class="primary-light elevation-1 pa-3 my-2">
            <div class="d-flex" style="min-height: 48px">
              <v-icon class="mr-3" style="font-size: 42px">
                $iconClipboard
              </v-icon>
              <div>{{ $t("people:personForm.Information.text") }}</div>
            </div>
          </div>
          <AppHeader>{{
            $t(
              "people:personForm.heading.general",
              "General",
              "Section heading for general fields"
            )
          }}</AppHeader>
          <v-text-field
            v-if="v$.form.firstName"
            v-model="form.firstName"
            :error-messages="appFormMixinValidationErrorsForField('firstName')"
            :disabled="appFormMixinIsFieldDisabled('firstName')"
            :label="
              $t(
                'people:personForm.input.FirstName.label',
                'First name',
                'Label of the first name field'
              )
            "
            data-test="personFirstName"
            required
            outlined
            @blur="v$.form.firstName.$touch()"
          />
          <v-text-field
            v-if="v$.form.lastName"
            v-model="form.lastName"
            :error-messages="appFormMixinValidationErrorsForField('lastName')"
            :disabled="appFormMixinIsFieldDisabled('lastName')"
            :label="
              $t(
                'people:personForm.input.LastName.label',
                'Last name',
                'Label of the last name field'
              )
            "
            data-test="personLastName"
            required
            outlined
            @blur="v$.form.lastName.$touch()"
          />

          <template v-if="v$.form.gender">
            <h3 class="mb-2">
              {{ $t("people:personForm.input.gender.label", "Gender") }}
            </h3>
            <v-radio-group
              v-model="form.gender"
              :error-messages="appFormMixinValidationErrorsForField('gender')"
              :disabled="appFormMixinIsFieldDisabled('gender')"
              class="mb-5"
            >
              <v-radio
                v-for="gender in genders"
                :key="gender.value"
                :value="gender.value"
                :label="gender.text"
              />
            </v-radio-group>
          </template>

          <v-row
            v-if="v$.form.email || v$.form.emailDescription"
            :no-gutters="$vuetify.breakpoint.xsOnly"
          >
            <v-col v-if="v$.form.email" cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                :error-messages="appFormMixinValidationErrorsForField('email')"
                :disabled="appFormMixinIsFieldDisabled('email')"
                :label="
                  $t(
                    'people:personForm.input.Email.label',
                    'Email',
                    'Label of the email field'
                  )
                "
                data-test="personEmail"
                required
                outlined
                @blur="v$.form.email.$touch()"
              />
            </v-col>
            <v-col v-if="v$.form.emailDescription" cols="12" sm="6">
              <v-text-field
                v-model="form.emailDescription"
                :error-messages="
                  appFormMixinValidationErrorsForField('emailDescription')
                "
                :disabled="appFormMixinIsFieldDisabled('emailDescription')"
                :label="
                  $t(
                    'people:personForm.input.emailDescription.label',
                    'Email description'
                  )
                "
                data-test="emailDescription"
                required
                outlined
                @blur="v$.form.emailDescription.$touch()"
              />
            </v-col>
          </v-row>

          <template v-if="v$.form.emailIsPublic">
            <v-checkbox
              v-model="form.emailIsPublic"
              data-test="personEmailPublic"
              :label="
                $t(
                  'people:personForm.input.emailIsPublic.label',
                  'Email is public',
                  'Label of the checkbox to mark if email is publicly visible'
                )
              "
              color="accent"
              class="mt-0"
              :disabled="appFormMixinIsFieldDisabled('emailIsPublic')"
            />
            <br />
          </template>
          <v-text-field
            v-if="v$.form.guardianEmail"
            v-model="form.guardianEmail"
            :error-messages="
              appFormMixinValidationErrorsForField('guardianEmail')
            "
            :disabled="appFormMixinIsFieldDisabled('guardianEmail')"
            :label="
              $t(
                'people:personForm.input.GuardianEmail.label',
                'Guardian email',
                'Label of the guardian email field'
              )
            "
            data-test="personGuardianEmail"
            required
            outlined
            @input="v$.form.guardianEmail.$touch()"
            @blur="v$.form.guardianEmail.$touch()"
          />

          <v-row
            v-if="v$.form.phone || v$.form.phoneDescription"
            :no-gutters="$vuetify.breakpoint.xsOnly"
          >
            <v-col v-if="v$.form.phone" cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                :error-messages="appFormMixinValidationErrorsForField('phone')"
                :disabled="appFormMixinIsFieldDisabled('phone')"
                :label="
                  $t(
                    'people:personForm.input.phone.label',
                    'Phone',
                    'Label of the phone field'
                  )
                "
                data-test="personPhone"
                required
                outlined
                @blur="v$.form.phone.$touch()"
              />
            </v-col>
            <v-col v-if="v$.form.phoneDescription" cols="12" sm="6">
              <v-text-field
                v-model="form.phoneDescription"
                :error-messages="
                  appFormMixinValidationErrorsForField('phoneDescription')
                "
                :disabled="appFormMixinIsFieldDisabled('phoneDescription')"
                :label="
                  $t(
                    'people:personForm.input.phoneDescription.label',
                    'Phone description'
                  )
                "
                data-test="phoneDescription"
                required
                outlined
                @blur="v$.form.phoneDescription.$touch()"
              />
            </v-col>
          </v-row>

          <template v-if="v$.form.phoneIsPublic">
            <v-checkbox
              v-model="form.phoneIsPublic"
              data-test="personPhonePublic"
              :label="
                $t(
                  'people:personForm.input.phoneIsPublic.label',
                  'Phone is public',
                  'Label of the checkbox to mark if phone number is publicly visible'
                )
              "
              color="accent"
              class="mt-0"
              :disabled="appFormMixinIsFieldDisabled('phoneIsPublic')"
            />
            <br />
          </template>
          <AppDatePicker
            v-model="form.dateOfBirth"
            :disabled="appFormMixinIsFieldDisabled('dateOfBirth')"
            :label="
              $t(
                'people:personForm.input.DateofBirth.label',
                'Date of birth',
                'Label of the date of birth field'
              )
            "
            data-test="datePicker"
            @blur="v$.form.dateOfBirth.$touch()"
          />

          <v-checkbox
            v-if="v$.form.deceased"
            v-model="form.deceased"
            data-test="deceased"
            :label="$t('people:personForm.input.deceased.label', 'Deceased')"
            color="accent"
            class="mb-5"
            :disabled="appFormMixinIsFieldDisabled('deceased')"
          />

          <div>
            <AppHeader>{{
              $t(
                "people:personForm.heading.identifiersLicence",
                "Bank details, identifiers and licence",
                "Section heading for Bank details, identifiers and licence fields"
              )
            }}</AppHeader>
            <div>
              <AppInputRegistryCode
                v-if="v$.form.registryCode"
                v-model="form.registryCode"
                :registry-country-id="form.registryCountryId"
                contact-type="PERSON"
                :input-props="{
                  label: $t(
                    'people:personForm.input.registryCode.label',
                    'Registry code'
                  ),
                  errorMessages:
                    appFormMixinValidationErrorsForField('registryCode'),
                  disabled: appFormMixinIsFieldDisabled('registryCode'),
                  dataTest: 'registryCode'
                }"
                @blur="v$.form.registryCode.$touch()"
              />

              <AppSelectCountries
                v-if="v$.form.registryCountryId"
                v-model="form.registryCountryId"
                :label="
                  $t(
                    'people:personForm.input.registryCountry.label',
                    'Registry country',
                    'Label of the country field'
                  )
                "
                :input-props="{
                  errorMessages:
                    appFormMixinValidationErrorsForField('registryCountryId'),
                  disabled: appFormMixinIsFieldDisabled('registryCountryId'),
                  dataTest: 'registryCountryId'
                }"
              />

              <v-col
                v-if="person.identifierSense"
                cols="12"
                md="6"
                class="px-0 py-0"
              >
                <v-text-field
                  :value="person.identifierSense"
                  :label="
                    $t(
                      'people:personForm.input.SENSEnr.label',
                      'SENSE nr',
                      'Label of the SENSE nr field'
                    )
                  "
                  outlined
                  data-test="personSENSE"
                  disabled
                />
              </v-col>

              <v-col
                v-if="person.licenceNumber"
                cols="12"
                md="6"
                class="px-0 py-0"
              >
                <v-text-field
                  :value="person.licenceNumber"
                  :label="
                    $t(
                      'people:personForm.input.licenceNr.label',
                      'Licence number',
                      'Label of the licence number field'
                    )
                  "
                  data-test="personLicence"
                  outlined
                  disabled
                />
              </v-col>
              <v-col
                v-if="form.licenceNumberStatus == 'active'"
                cols="12"
                md="6"
                class="px-0 py-0"
              >
                <v-autocomplete
                  v-if="v$.form.licenceCategoryId"
                  v-model="form.licenceCategoryId"
                  :items="masterLicenceCategoriesActive"
                  :disabled="appFormMixinIsFieldDisabled('licenceCategoryId')"
                  :clearable="!appFormMixinIsFieldDisabled('licenceCategoryId')"
                  :error-messages="
                    appFormMixinValidationErrorsForField('licenceCategoryId')
                  "
                  data-test="personLicenceCategory"
                  :label="
                    $t(
                      'people:personForm.input.licenceCategory.label',
                      'Licence category',
                      'Label of the licence category field'
                    )
                  "
                  item-value="id"
                  item-text="name"
                  outlined
                />
              </v-col>

              <v-col cols="12" md="6" class="px-0 py-0">
                <v-text-field
                  v-if="v$.form.feiid"
                  v-model="form.feiid"
                  :error-messages="
                    appFormMixinValidationErrorsForField('feiid')
                  "
                  :clearable="!appFormMixinIsFieldDisabled('feiid')"
                  :disabled="appFormMixinIsFieldDisabled('feiid')"
                  :label="
                    $t(
                      'people:personForm.input.FEIID.label',
                      'FEI ID',
                      'Label of the FEI ID field'
                    )
                  "
                  data-test="feiid"
                  reactive
                  outlined
                  @blur="v$.form.feiid.$touch()"
                />

                <AppSelectCountries
                  v-if="v$.form.representingCountryId"
                  v-model="form.representingCountryId"
                  :label="
                    $t(
                      'people:personForm.input.representingCountry.label',
                      'Representing country',
                      'Label of the country field'
                    )
                  "
                  :input-props="{
                    errorMessages: appFormMixinValidationErrorsForField(
                      'representingCountryId'
                    ),
                    disabled: appFormMixinIsFieldDisabled(
                      'representingCountryId'
                    ),
                    dataTest: 'representingCountryId'
                  }"
                />

                <AppDatePicker
                  v-if="v$.form.foreignCertificateDate"
                  v-model="form.foreignCertificateDate"
                  :disabled="
                    appFormMixinIsFieldDisabled('foreignCertificateDate')
                  "
                  :error-messages="
                    appFormMixinValidationErrorsForField(
                      'foreignCertificateDate'
                    )
                  "
                  :label="
                    $t(
                      'people:personForm.input.foreignCertificateDate.label',
                      'Competition permit certificate'
                    )
                  "
                  :hint="
                    $t(
                      'people:personForm.input.foreignCertificateDate.hint',
                      'Enter the date for the last valid certificate by an athlete´s home federation'
                    )
                  "
                  data-test="foreignCertificateDate"
                  @blur="v$.form.foreignCertificateDate.$touch()"
                />
              </v-col>
            </div>
          </div>

          <div v-if="additionalCommunications">
            <AppHeader>{{
              $t(
                "people:personForm.heading.additionalCommunicationOptions",
                "Additional communication options"
              )
            }}</AppHeader>

            <div>
              <v-row
                v-if="v$.form.email2 || v$.form.email2Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.email2" cols="12" sm="6">
                  <v-text-field
                    v-model="form.email2"
                    :error-messages="
                      appFormMixinValidationErrorsForField('email2')
                    "
                    :disabled="appFormMixinIsFieldDisabled('email2')"
                    :label="
                      $t(
                        'people:personForm.input.email2.label',
                        'Email 2',
                        'Label of the email field'
                      )
                    "
                    data-test="personEmail2"
                    required
                    outlined
                    @blur="v$.form.email2.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.email2Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.email2Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('email2Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('email2Description')"
                    :label="
                      $t(
                        'people:personForm.input.email2Description.label',
                        'Email 2 description'
                      )
                    "
                    data-test="email2Description"
                    outlined
                    @blur="v$.form.email2Description.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.email3 || v$.form.email3Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.email3" cols="12" sm="6">
                  <v-text-field
                    v-model="form.email3"
                    :error-messages="
                      appFormMixinValidationErrorsForField('email3')
                    "
                    :disabled="appFormMixinIsFieldDisabled('email3')"
                    :label="
                      $t(
                        'people:personForm.input.email3.label',
                        'Email 3',
                        'Label of the email field'
                      )
                    "
                    data-test="personEmail3"
                    outlined
                    @blur="v$.form.email3.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.email3Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.email3Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('email3Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('email3Description')"
                    :label="
                      $t(
                        'people:personForm.input.email3Description.label',
                        'Email 3 description'
                      )
                    "
                    data-test="email3Description"
                    outlined
                    @blur="v$.form.email3Description.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.phone2 || v$.form.phone2Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.phone2" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone2"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone2')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone2')"
                    :label="
                      $t(
                        'people:personForm.input.phone2.label',
                        'Phone 2',
                        'Label of the phone field'
                      )
                    "
                    data-test="personPhone2"
                    required
                    outlined
                    @blur="v$.form.phone2.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.phone2Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone2Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone2Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone2Description')"
                    :label="
                      $t(
                        'people:personForm.input.phone2Description.label',
                        'Phone 2 description'
                      )
                    "
                    data-test="phone2Description"
                    outlined
                    @blur="v$.form.phone2Description.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.phone3 || v$.form.phone3Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.phone3" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone3"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone3')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone3')"
                    :label="
                      $t(
                        'people:personForm.input.phone3.label',
                        'Phone 3',
                        'Label of the phone field'
                      )
                    "
                    data-test="personPhone3"
                    outlined
                    @blur="v$.form.phone3.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.phone3Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone3Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone3Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone3Description')"
                    :label="
                      $t(
                        'people:personForm.input.phone3Description.label',
                        'Phone 3 description'
                      )
                    "
                    data-test="phone3Description"
                    outlined
                    @blur="v$.form.phone3Description.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.phone4 || v$.form.phone4Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.phone4" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone4"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone4')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone4')"
                    :label="
                      $t(
                        'people:personForm.input.phone4.label',
                        'Phone 4',
                        'Label of the phone field'
                      )
                    "
                    data-test="personPhone4"
                    outlined
                    @blur="v$.form.phone4.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.phone4Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.phone4Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('phone4Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('phone4Description')"
                    :label="
                      $t(
                        'people:personForm.input.phone4Description.label',
                        'Phone 4 description'
                      )
                    "
                    data-test="phone4Description"
                    outlined
                    @blur="v$.form.phone4Description.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.fax || v$.form.fax"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.fax" cols="12" sm="6">
                  <v-text-field
                    v-model="form.fax"
                    :error-messages="
                      appFormMixinValidationErrorsForField('fax')
                    "
                    :disabled="appFormMixinIsFieldDisabled('fax')"
                    :label="$t('people:personForm.input.fax.label', 'Fax')"
                    data-test="fax"
                    outlined
                    @blur="v$.form.fax.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.faxDescription" cols="12" sm="6">
                  <v-text-field
                    v-model="form.faxDescription"
                    :error-messages="
                      appFormMixinValidationErrorsForField('faxDescription')
                    "
                    :disabled="appFormMixinIsFieldDisabled('faxDescription')"
                    :label="
                      $t(
                        'people:personForm.input.faxDescription.label',
                        'Fax description'
                      )
                    "
                    data-test="faxDescription"
                    outlined
                    @blur="v$.form.faxDescription.$touch()"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="v$.form.fax2 || v$.form.fax2Description"
                :no-gutters="$vuetify.breakpoint.xsOnly"
              >
                <v-col v-if="v$.form.fax2" cols="12" sm="6">
                  <v-text-field
                    v-model="form.fax2"
                    :error-messages="
                      appFormMixinValidationErrorsForField('fax2')
                    "
                    :disabled="appFormMixinIsFieldDisabled('fax2')"
                    :label="$t('people:personForm.input.fax2.label', 'Fax 2')"
                    data-test="fax2"
                    outlined
                    @blur="v$.form.fax2.$touch()"
                  />
                </v-col>
                <v-col v-if="v$.form.fax2Description" cols="12" sm="6">
                  <v-text-field
                    v-model="form.fax2Description"
                    :error-messages="
                      appFormMixinValidationErrorsForField('fax2Description')
                    "
                    :disabled="appFormMixinIsFieldDisabled('fax2Description')"
                    :label="
                      $t(
                        'people:personForm.input.fax2Description.label',
                        'Fax 2 description'
                      )
                    "
                    data-test="fax2Description"
                    outlined
                    @blur="v$.form.fax2Description.$touch()"
                  />
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-if="v$.form.address" data-test="personAddress">
            <AppHeader>{{
              $t("people:personForm.heading.address", "Home address")
            }}</AppHeader>
            <AppAddress
              :address-object="form.address"
              :disabled="appFormMixinIsFieldDisabled('address')"
              :contactable-id="person.id"
              contactable-type="Person"
              contactable-association-name="address"
              :parent-form-name="initPersonForm.formName"
              hide-edit-heading
              @set-address="address => (form.address = address)"
            />
          </div>

          <div v-if="v$.form.addressForCorrespondence">
            <AppHeader>{{
              $t(
                "people:personForm.heading.addressForCorrespondence",
                "Correspondence address"
              )
            }}</AppHeader>
            <AppAddress
              :address-object="form.addressForCorrespondence"
              :disabled="
                appFormMixinIsFieldDisabled('addressForCorrespondence')
              "
              :contactable-id="person.id"
              contactable-type="Person"
              contactable-association-name="addressForCorrespondence"
              :parent-form-name="initPersonForm.formName"
              hide-edit-heading
              @set-address="
                address => (form.addressForCorrespondence = address)
              "
            />
          </div>

          <div>
            <AppHeader>{{
              $t(
                "people:personForm.otherInformation.heading",
                "Other information"
              )
            }}</AppHeader>

            <div>
              <v-select
                v-if="v$.form.defaultLocale"
                v-model="form.defaultLocale"
                :items="defaultLocales"
                :label="
                  $t(
                    'people:personForm.input.language.label',
                    'Preferred language'
                  )
                "
                :hint="
                  $t(
                    'people:personForm.input.language.hint',
                    'Select the main preferred language of communications'
                  )
                "
                persistent-hint
                :error-messages="
                  appFormMixinValidationErrorsForField('defaultLocale')
                "
                :disabled="appFormMixinIsFieldDisabled('defaultLocale')"
                outlined
                @blur="v$.form.defaultLocale.$touch()"
              />

              <v-text-field
                v-if="v$.form.website"
                v-model="form.website"
                :label="$t('people:personForm.input.website.label', 'Website')"
                :error-messages="
                  appFormMixinValidationErrorsForField('website')
                "
                :disabled="appFormMixinIsFieldDisabled('website')"
                outlined
                @blur="v$.form.website.$touch()"
              />

              <v-textarea
                v-if="v$.form.internalNote"
                v-model="form.internalNote"
                :label="
                  $t(
                    'people:personForm.input.internalNote.label',
                    'Internal note'
                  )
                "
                :error-messages="
                  appFormMixinValidationErrorsForField('internalNote')
                "
                :disabled="appFormMixinIsFieldDisabled('internalNote')"
                outlined
                @blur="v$.form.internalNote.$touch()"
              />
            </div>
          </div>

          <div ref="duplicates">
            <PersonDuplicatesTable
              v-if="possibleDuplicatePeople.length > 0"
              :duplicates="possibleDuplicatePeople"
            />
          </div>

          <v-row dense>
            <v-col
              :class="$vuetify.breakpoint.smAndUp ? 'px-5' : 'px-2'"
              cols="12"
              md="6"
            >
              <v-btn
                :to="
                  personId
                    ? {
                        name: 'PersonProfilePage',
                        params: { personId: personId }
                      }
                    : { name: 'PeopleSearchPage' }
                "
                block
                depressed
                :disabled="formSubmitted"
                class="entsys--btn--round entsys--btn--border"
                active-class
                >{{
                  $t("common:button.cancel", "Cancel", "Cancel button")
                }}</v-btn
              >
            </v-col>
            <v-col
              :class="$vuetify.breakpoint.smAndUp ? 'px-5' : 'px-2'"
              cols="12"
              md="6"
            >
              <v-btn
                block
                class="entsys--btn--round"
                color="accent"
                data-test="personSubmit"
                :loading="formSubmitted"
                @click="onSubmit"
                >{{ $t("common:button.save", "Save", "Save button") }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import gql from "fraql";
import i18next from "i18next";
import { trim, get } from "lodash-es";
import saveMeta from "@/mixins/save_meta";
import { useLicenceCategoryHelper } from "@composables/masterdata/person/licenceCategories";
import { getFederationConfiguration } from "@composables/masterdata/config";
import appFormMixin from "@/mixins/appFormMixin";

import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppHeader from "@elements/AppHeader.vue";
import PersonDuplicatesTable from "./PersonDuplicatesTable.vue";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppSelectCountries from "@AppSelects/AppSelectCountries.vue";
import AppAddress, {
  prepAddressForAddressInput
} from "@elements/address/AppAddress.vue";
import AppInputRegistryCode from "@AppInputs/AppInputRegistryCode.vue";
import { useFormFieldPossibleTranslation } from "@composables/formFieldPossibleValuesTranslation";
import FipaListener from "@/utils/listenerUtility";

const PERSON = gql`
  fragment _ on Person {
    id
    name
    firstName
    lastName
    dateOfBirth
    email
    guardianEmail
    phone
    feiid
    
    address {
      ${AppAddress.fragments.addressFields}
    }
    addressForCorrespondence {
      ${AppAddress.fragments.addressFields}
    }

    foreignCertificateDate

    gender
    emailDescription
    phoneDescription
    deceased

    registryCode
    registryCountryId
    representingCountryId 

    email2
    email2Description
    email3
    email3Description

    phone2
    phone2Description
    phone3
    phone3Description
    phone4
    phone4Description

    fax
    faxDescription
    fax2
    fax2Description

    defaultLocale
    website
    internalNote

    licenceNumber
    licenceNumberStatus

    activeIdentifier
    identifierSense
    identifierSenseStatus

    phoneIsPublic
    emailIsPublic

    licenceCategoryName
    licenceCategoryId
    licenceCategory {
      id
      name
    }
  }
`;

export default {
  name: "PersonForm",
  components: {
    AppHeader,
    AppLoadingPage,
    AppDatePicker,
    AppSelectCountries,
    AppAddress,
    AppInputRegistryCode,
    PersonDuplicatesTable
  },
  mixins: [appFormMixin, saveMeta],
  props: {
    personId: {
      type: String,
      default: null
    }
  },
  setup() {
    const appForm = appFormMixin.setup();
    const { formFieldPossibleTranslation } = useFormFieldPossibleTranslation();

    return {
      ...getFederationConfiguration(),
      ...appForm,
      formFieldPossibleTranslation,
      ...useLicenceCategoryHelper()
    };
  },
  data() {
    return {
      person: {
        id: this.personId
      },
      formSubmitted: false,
      form: { bankAccountIban: "" },
      initPersonForm: {
        formFields: [],
        vuelidateValidations: {},
        readOnlyFields: []
      },
      possibleDuplicatePeople: [],
      formError: {},
      appFormMixinValidationsConfVariable:
        "initPersonForm.vuelidateValidations",
      appFormMixinReadOnlyFieldsVariable: "initPersonForm.readOnlyFields"
    };
  },
  computed: {
    information: () => i18next.exists("people:personForm.Information.text"),
    genders() {
      return this.formFieldPossibleTranslation(
        this.initPersonForm?.formFields?.find(x => x.name == "gender")
          ?.possibleValues ?? []
      );
    },
    defaultLocales() {
      return this.formFieldPossibleTranslation(
        this.initPersonForm?.formFields?.find(x => x.name == "defaultLocale")
          ?.possibleValues ?? []
      );
    },
    additionalCommunications() {
      return Object.keys(this.initPersonForm?.vuelidateValidations ?? {}).some(
        x =>
          [
            "email2",
            "email2Description",
            "email3",
            "email3Description",

            "phone2",
            "phone2Description",
            "phone3",
            "phone3Description",
            "phone4",
            "phone4Description",

            "fax",
            "faxDescription",
            "fax2",
            "fax2Description2"
          ].includes(x)
      );
    }
  },
  fragments: {
    person: PERSON
  },
  apollo: {
    person: {
      query() {
        return gql`
          query person ($id: ID!) {
            person (id: $id) {
              ${PERSON}
            }
          }
        `;
      },
      variables() {
        return {
          id: this.personId
        };
      },
      result({ data }) {
        if (data) {
          this.$set(this, "form", JSON.parse(JSON.stringify(data.person)));
          if (data && data.person) {
            FipaListener.emit("breadcrumbPageName", {
              name: data.person.name,
              id: data.person.id,
              breadcrumbName: "personName"
            });
          }
        }
      },
      skip() {
        return !this.personId;
      }
    },
    initPersonForm: {
      query: gql`
        query initPersonForm($id: ID) {
          initPersonForm(id: $id) {
            formFields {
              name
              possibleValues
            }
            formName
            readOnlyFields
            vuelidateValidations
          }
        }
      `,
      variables() {
        return {
          id: this.personId
        };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        this.initPersonForm = data.initPersonForm;
      }
    }
  },
  methods: {
    trim,
    async onSubmit() {
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        this.$root.$showFeedback.open("warn", {
          messages: this.formError
        });
        return false;
      }

      this.$root.$showFeedback.close();

      this.formSubmitted = true;

      let variables = this.form;
      variables.id = this.personId;

      // in case we have shown possible duplicates then saving the form will also confirm that we create duplicate
      variables.confirmCreatingDuplicate =
        this.possibleDuplicatePeople.length > 0;

      variables.address = prepAddressForAddressInput(variables.address);
      variables.addressForCorrespondence = prepAddressForAddressInput(
        variables.addressForCorrespondence
      );

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation personCreateOrUpdate (
              $id: ID
              $firstName: String
              $lastName: String
              $email: String
              $guardianEmail: String
              $phone: String
              $dateOfBirth: String
              $licenceCategoryId: ID
              $feiid: String
              $confirmCreatingDuplicate: Boolean

              $foreignCertificateDate: Date


              $phoneIsPublic: Boolean
              $emailIsPublic: Boolean

              $defaultLocale: String
              $website: String
              $internalNote: String

              $gender: String
              $emailDescription: String
              $phoneDescription: String
              $deceased: Boolean

              $registryCode: String
              $registryCountryId: ID
              $representingCountryId: ID

              $email2: String
              $email2Description: String
              $email3: String
              $email3Description: String

              $phone2: String
              $phone2Description: String
              $phone3: String
              $phone3Description: String
              $phone4: String
              $phone4Description: String

              $fax: String
              $faxDescription: String
              $fax2: String
              $fax2Description: String

              $address: AddressInput
              $addressForCorrespondence: AddressInput
            ) {
              personCreateOrUpdate (input: {
                id: $id,
                firstName: $firstName,
                lastName: $lastName,
                email: $email,
                guardianEmail: $guardianEmail
                phone: $phone,
                dateOfBirth: $dateOfBirth,
                licenceCategoryId: $licenceCategoryId,
                feiid: $feiid,
                confirmCreatingDuplicate: $confirmCreatingDuplicate,

                foreignCertificateDate: $foreignCertificateDate

                gender: $gender
                deceased: $deceased
                registryCode: $registryCode
                registryCountryId: $registryCountryId
                representingCountryId: $representingCountryId
                
                phoneIsPublic: $phoneIsPublic
                emailIsPublic: $emailIsPublic
                defaultLocale: $defaultLocale
                website: $website
                internalNote: $internalNote

                emailDescription: $emailDescription
                phoneDescription: $phoneDescription

                email2: $email2
                email2Description: $email2Description
                email3: $email3
                email3Description: $email3Description
                phone2: $phone2
                phone2Description: $phone2Description
                phone3: $phone3
                phone3Description: $phone3Description
                phone4: $phone4
                phone4Description: $phone4Description

                fax: $fax
                faxDescription: $faxDescription
                fax2: $fax2
                fax2Description: $fax2Description

                address: $address
                addressForCorrespondence: $addressForCorrespondence
              }) {
                person {
                  ${PERSON}
                }
                possibleDuplicates {
                  id
                  ${PersonDuplicatesTable.fragments.personFields}
                }
              }
            }
        `,
          variables: variables
        });

        if (
          get(data, "personCreateOrUpdate.possibleDuplicates.length", false)
        ) {
          this.possibleDuplicatePeople =
            data.personCreateOrUpdate.possibleDuplicates;
          this.$nextTick(() => {
            this.$refs.duplicates.scrollIntoView({ behavior: "smooth" });
          });
        } else {
          this.saveMetaSetOriginalForm(this.form);
          let text = this.form.id
            ? "Changes saved successfully!"
            : "Person account created successfully!";

          if (this.personId) {
            const person = get(data, "personCreateOrUpdate.person");
            if (this.$auth.user && person.id === this.$auth.user.id) {
              this.$auth.userName = person.name;
            }
          }

          this.$root.$snackbar("success", {
            text: text,
            redirection: true
          });

          this.$router.push({
            name: "PersonProfilePage",
            params: { personId: data.personCreateOrUpdate.person.id }
          });
        }
      } catch (error) {
        // Error
        this.$root.$showFeedback.showGraphqlError(error);
        this.$sentryThrowError.throwError(error, {
          level: "error",
          additionalDetails: { from: "PersonForm" }
        });
      } finally {
        this.formSubmitted = false;
      }
    }
  }
};
</script>
